import { useCallback, useRef, useState } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { toast } from 'sonner'

import { clearPursuitCaches } from '@/components/chat-v2/hooks/usePursuitSummaryCache'

import { useOrganization } from '@/hooks/organization/useOrganization'
import { useOrganizations } from '@/hooks/organization/useOrganizations'
import { useUpdateDefaultOrganization } from '@/hooks/organization/useUpdateDefaultOrganization'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { Organization } from '@/types/Organization'

export const ORG_SWITCHED_SESSION_KEY = 'sweetspot:org-switched'

interface UseCuiOrgSwitcherOptions {
  redirectTo?: string | ((targetOrgId: UUID) => string)
  onBeforeSwitch?: (targetOrg: Organization) => void
}

function resolveRedirect(
  redirectTo: string | ((targetOrgId: UUID) => string) | undefined,
  targetOrgId: UUID,
): string {
  if (typeof redirectTo === 'function') return redirectTo(targetOrgId)
  if (typeof redirectTo === 'string') return redirectTo

  const pathname = window.location.pathname
  const orgScopedMatch = pathname.match(/^\/orgs\/[^/]+(.*)$/)
  if (orgScopedMatch) {
    return `/orgs/${targetOrgId}${orgScopedMatch[1] || '/library'}`
  }
  return pathname
}

export interface UseCuiOrgSwitcherResult {
  isLoading: boolean
  isCompliant: boolean
  switchableOrganizations: Organization[]
  hasSingleSwitchableOrganization: boolean
  hasMultipleSwitchableOrganizations: boolean
  singleSwitchableOrganization: Organization | null
  popoverOpen: boolean
  setPopoverOpen: (open: boolean) => void
  handleSwitchOrganization: (targetOrgId: UUID) => void
  handleSwitchToSingleOrganization: () => void
}

interface UseCuiOrgSwitcherArgs {
  orgId: UUID
  options?: UseCuiOrgSwitcherOptions
}

export function useCuiOrgSwitcher({
  orgId,
  options,
}: UseCuiOrgSwitcherArgs): UseCuiOrgSwitcherResult {
  const { data: organization, isLoading: isOrgLoading } = useOrganization(orgId)
  const { mutateAsync: updateDefaultOrganization } =
    useUpdateDefaultOrganization()

  const redirectToRef = useRef(options?.redirectTo)
  redirectToRef.current = options?.redirectTo
  const onBeforeSwitchRef = useRef(options?.onBeforeSwitch)
  onBeforeSwitchRef.current = options?.onBeforeSwitch

  const isCompliant = organization?.cui_compliance_enabled || false

  const selectSwitchableOrganizations = useCallback(
    (data: InfiniteData<PaginatedResponse<Organization>, PaginateRequest>) => {
      const organizations = data.pages.map((page) => page.data).flat()
      return organizations
        .filter(
          (candidate) =>
            candidate.id !== orgId &&
            candidate.cui_compliance_enabled !== isCompliant,
        )
        .sort((a, b) => a.name.localeCompare(b.name))
    },
    [orgId, isCompliant],
  )

  const { data: switchableOrganizations, isLoading: isOrganizationsLoading } =
    useOrganizations({ select: selectSwitchableOrganizations })

  const hasSingleSwitchableOrganization =
    switchableOrganizations && switchableOrganizations?.length === 1
  const hasMultipleSwitchableOrganizations =
    switchableOrganizations && switchableOrganizations?.length > 1
  const singleSwitchableOrganization =
    switchableOrganizations && (switchableOrganizations[0] ?? null)

  const [popoverOpen, setPopoverOpen] = useState(false)

  const handleSwitchOrganization = useCallback(
    async (targetOrgId: UUID) => {
      if (!switchableOrganizations) return
      const targetOrg = switchableOrganizations.find(
        (o) => o.id === targetOrgId,
      )
      if (!targetOrg) return

      onBeforeSwitchRef.current?.(targetOrg)
      setPopoverOpen(false)
      clearPursuitCaches()

      const destination = resolveRedirect(redirectToRef.current, targetOrgId)

      const destinationIsOrgScoped = destination.startsWith(
        `/orgs/${targetOrgId}`,
      )

      try {
        await updateDefaultOrganization([targetOrg])
        try {
          sessionStorage.setItem(ORG_SWITCHED_SESSION_KEY, '1')
        } catch {}
        window.location.assign(destination)
      } catch {
        if (destinationIsOrgScoped) {
          window.location.assign(destination)
          return
        }
        toast.error('Failed to switch organizations. Please try again.')
      }
    },
    [switchableOrganizations, updateDefaultOrganization],
  )

  const handleSwitchToSingleOrganization = useCallback(() => {
    if (!singleSwitchableOrganization) {
      return
    }
    handleSwitchOrganization(singleSwitchableOrganization.id)
  }, [handleSwitchOrganization, singleSwitchableOrganization])

  return {
    isLoading: isOrgLoading || isOrganizationsLoading,
    isCompliant,
    switchableOrganizations: switchableOrganizations ?? [],
    hasSingleSwitchableOrganization: hasSingleSwitchableOrganization ?? false,
    hasMultipleSwitchableOrganizations:
      hasMultipleSwitchableOrganizations ?? false,
    singleSwitchableOrganization: singleSwitchableOrganization ?? null,
    popoverOpen,
    setPopoverOpen,
    handleSwitchOrganization,
    handleSwitchToSingleOrganization,
  }
}
